import React, { useState, useEffect } from 'react';
import api from '../services/api';

const QuickEditModal = ({ vehicle, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    vin: '',
    purchasePrice: '',
    reconCost: '',
    transportCost: '',
    estimatedSale: '',
    titleStatus: '',
    endUse: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when vehicle changes
  useEffect(() => {
    if (vehicle) {
      setFormData({
        vin: vehicle.vin || '',
        purchasePrice: vehicle.purchasePrice || '',
        reconCost: vehicle.reconCost || '',
        transportCost: vehicle.transportCost || '',
        estimatedSale: vehicle.estimatedSale || '',
        titleStatus: vehicle.titleStatus || '',
        endUse: vehicle.endUse || '',
        notes: vehicle.notes || ''
      });
    }
  }, [vehicle]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuickAction = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!vehicle) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // Helper to parse price - handles both string and number inputs
      const parsePrice = (val) => {
        if (val === '' || val === null || val === undefined) return null;
        if (typeof val === 'number') return val;
        return parseFloat(String(val).replace(/[$,]/g, '')) || null;
      };

      // Convert prices to numbers
      const updateData = {
        vin: formData.vin.trim().toUpperCase() || null,
        purchasePrice: parsePrice(formData.purchasePrice),
        reconCost: parsePrice(formData.reconCost),
        transportCost: parsePrice(formData.transportCost),
        estimatedSale: parsePrice(formData.estimatedSale),
        titleStatus: formData.titleStatus || null,
        endUse: formData.endUse || null,
        notes: formData.notes || null
      };

      // Remove null values but keep 0 values (user might want to set cost to 0)
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null) {
          delete updateData[key];
        }
      });

      await api.updateVehicle(vehicle.id, updateData);
      await (typeof onUpdate === 'function' ? Promise.resolve(onUpdate()) : Promise.resolve());
      onClose();
    } catch (err) {
      console.error('Quick edit error:', err);
      setError('Failed to update vehicle: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !vehicle) return null;

  // Check if this is an uploaded vehicle (no dash in stock number)
  const isUploadedVehicle = vehicle.stockNumber && !vehicle.stockNumber.includes('-');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">Quick Edit Vehicle</h2>
            <p className="text-sm text-gray-600">
              {vehicle.year} {vehicle.make} {vehicle.model} - Stock: {vehicle.stockNumber}
              {isUploadedVehicle && <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Uploaded Inventory</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* VIN */}
          <div>
            <h3 className="text-lg font-medium mb-3">VIN</h3>
            <input
              type="text"
              value={formData.vin}
              onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
              placeholder="17-character VIN"
              maxLength={25}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
            {formData.vin && formData.vin.length !== 17 && (
              <p className="text-xs text-amber-600 mt-1">{formData.vin.length} characters — standard VINs are 17</p>
            )}
          </div>

          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-medium mb-3">Financial Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Price
                </label>
                <input
                  type="text"
                  value={formData.purchasePrice}
                  onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Sale Price
                </label>
                <input
                  type="text"
                  value={formData.estimatedSale}
                  onChange={(e) => handleInputChange('estimatedSale', e.target.value)}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recon Cost
                </label>
                <input
                  type="text"
                  value={formData.reconCost}
                  onChange={(e) => handleInputChange('reconCost', e.target.value)}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transport Cost
                </label>
                <input
                  type="text"
                  value={formData.transportCost}
                  onChange={(e) => handleInputChange('transportCost', e.target.value)}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Title Status */}
          <div>
            <h3 className="text-lg font-medium mb-3">Title Status</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => handleQuickAction('titleStatus', 'in_office')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.titleStatus === 'in_office'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                In Office
              </button>
              <button
                onClick={() => handleQuickAction('titleStatus', 'pending')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.titleStatus === 'pending'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => handleQuickAction('titleStatus', 'unknown')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.titleStatus === 'unknown'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Unknown
              </button>
            </div>
            <select
              value={formData.titleStatus}
              onChange={(e) => handleInputChange('titleStatus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Title Status</option>
              <option value="in_office">In Office</option>
              <option value="pending">Pending</option>
              <option value="unknown">Unknown</option>
              <option value="lost">Lost</option>
              <option value="duplicate_requested">Duplicate Requested</option>
            </select>
          </div>

          {/* Vehicle Category */}
          <div>
            <h3 className="text-lg font-medium mb-3">Vehicle Category</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => handleQuickAction('endUse', 'dealer_direct')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.endUse === 'dealer_direct'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Byrider/DD
              </button>
              <button
                onClick={() => handleQuickAction('endUse', 'shop_personal')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.endUse === 'shop_personal'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Shop/Personal
              </button>
              <button
                onClick={() => handleQuickAction('endUse', 'retail')}
                className={`px-3 py-2 rounded text-sm font-medium ${
                  formData.endUse === 'retail' || !formData.endUse
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Retail
              </button>
            </div>
            <select
              value={formData.endUse}
              onChange={(e) => handleInputChange('endUse', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Category</option>
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
              <option value="dealer_direct">Byrider/Dealer Direct</option>
              <option value="shop_personal">Shop/Personal</option>
              <option value="copart">Copart</option>
              <option value="iaai">IAAI</option>
              <option value="parts_only">Parts Only</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes Section */}
          <div>
            <h3 className="text-lg font-medium mb-3">Notes</h3>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Add any notes or comments about this vehicle..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickEditModal;
